import { useMemo } from 'react';

type RawData = Record<string, any>;

export type TreeSourceData = RawData[];

export interface TreeNode {
  value: number;
  name: string;
  raw: any;
  children?: TreeNode[];
}

export interface TreeRootNode {
  value: number;
  children: TreeNode[];
  partition: string | number | boolean;
  name: string;
  color?: string;
}

export interface TreeDataProps {
  data?: TreeSourceData;
  labelField: string;
  valueField: string;
  partitionBy: string | ((item: RawData) => string | number | boolean);
  colorMap: Record<string, string>;
  drillDown: boolean;
  total?: Record<string, number>;
}

const DRILL_DOWN_ICON = '▶';

function normal(value: any) {
  return Number(value) || 0;
}

function labelFormat(value: any = '', drillDown: boolean) {
  const prefix = drillDown ? `${DRILL_DOWN_ICON} ` : '';
  if (Array.isArray(value)) {
    return `${prefix}${value.join('')}`
  }
  return `${prefix}${value.toString()}`;
}

function createRootNode(item: RawData, labelField: string, valueField: string, partitionBy: (item: RawData) => TreeRootNode['partition'], drillDown: boolean) {
  const partition = partitionBy(item);
  const rootNode = {
    value: normal(item[valueField]),
    name: partition,
    children: [],
    partition,
  } as TreeRootNode;

  const childNode = {
    value: normal(item[valueField]),
    name: labelFormat(item[labelField], drillDown),
    raw: item[labelField],
  } as TreeNode

  rootNode.children.push(childNode);
  return rootNode;
}

function insertChildNode(parent: TreeRootNode, item: RawData, labelField: string, valueField: string, drillDown: boolean) {
  const value = normal(item[valueField]);

  parent.value += value;
  const childNode = {
    value,
    name: labelFormat(item[labelField], drillDown),
    raw: item[labelField],
  } as TreeNode

  parent.children.push(childNode)
}

function createTreeData({ data, labelField, valueField, partitionBy, drillDown }: {
  data: TreeSourceData;
  labelField: string;
  valueField: string;
  partitionBy: (item: RawData) => TreeRootNode['partition'];
  drillDown: boolean;
}) {
  const tree: TreeRootNode[] = [];
  data.forEach((item) => {
    const parent = tree.find((node) => node.partition === partitionBy(item));
    if (parent) {
      insertChildNode(parent, item, labelField, valueField, drillDown);
    } else {
      const node = createRootNode(item, labelField, valueField, partitionBy, drillDown);
      tree.push(node);
    }
  });
  return tree.sort((a, b) => b.value - a.value);
}

export function useFlatTreeData({ data, labelField, partitionBy, valueField, colorMap, drillDown}: TreeDataProps) {
  const partitionByFunc = useMemo(() => {
    if (typeof partitionBy === 'string') {
      return (item: RawData) => item[partitionBy];
    }
    return partitionBy;
  }, [partitionBy]);

  const tree = useMemo(() => {
    const treeData = createTreeData({
      data: data || [],
      labelField,
      valueField,
      partitionBy: partitionByFunc,
      drillDown
    });
    treeData.forEach((node) => {
      node.color = colorMap[node.name];
    });
    return treeData;
  }, [data, labelField, valueField, partitionByFunc, drillDown, colorMap]);

  return {
    tree,
  }
}
